import type { TDocRef } from "@api/access";
import { FEATURE_FLAGS, useFeatureFlag } from "@lib/feature-flags";
import { useAppSelector } from "@store";
import { getDocAccess, getStoryId, getVersionId } from "@store/story/selectors";
import { useMemo } from "react";

/**
 * Send flow P5: what the caller may do in the open document. Viewers and
 * commenters get a read-only editor (commenters keep the comment triggers).
 * Flag off = today's full editor. Every editor element calls this, so it
 * subscribes to one stored value and derives the rest.
 */
export const useDocPermissions = () => {
  const isSendFlow = useFeatureFlag(FEATURE_FLAGS.SEND_FLOW);
  const access = useAppSelector(getDocAccess);
  // A missing role is today's behaviour, i.e. full editor.
  const role = (isSendFlow ? access?.myRole : undefined) ?? "owner";
  return {
    role,
    isGuest: isSendFlow && !!access?.isGuest,
    canEdit: role === "owner" || role === "editor",
    canComment: role !== "viewer",
  };
};

/** The open document for the access endpoints: its version, else its story. */
export const useDocRef = () => {
  const storyId = useAppSelector(getStoryId);
  const versionId = useAppSelector(getVersionId);
  return useMemo<TDocRef | null>(
    () =>
      versionId
        ? { docType: "version", docId: versionId }
        : storyId
          ? { docType: "page", docId: storyId }
          : null,
    [storyId, versionId],
  );
};
